<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from 'dashboard/routes/dashboard/projects/ProjectEmptyState.vue';

// Generic document-request checklist panel. The host injects an `api` adapter
// (get/create/update/delete/reorder/accept/reject — each taking ownerId first),
// the `ownerId`, and a `shareFn(ownerId)` returning { data: { document_request_token } }.
const props = defineProps({
  api: {
    type: Object,
    required: true,
  },
  ownerId: {
    type: [String, Number],
    required: true,
  },
  shareFn: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['count']);

const { t } = useI18n();

const TYPE_KEYS = [
  'acta_constitutiva',
  'poder_notarial',
  'nda',
  'contrato',
  'propuesta',
  'factura',
  'identificacion',
  'otro',
];

const STATUS_PILL = {
  pending: 'bg-n-alpha-2 text-n-slate-11',
  submitted: 'bg-n-amber-3 text-n-amber-11',
  accepted: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
};

const requests = ref([]);
const isLoading = ref(false);
const isCopying = ref(false);

const showForm = ref(false);
const isSaving = ref(false);
const emptyForm = () => ({ label: '', document_type: 'otro', required: true, parts: [] });
const form = ref(emptyForm());

const editingId = ref(null);
const editForm = ref(emptyForm());

// --- Sides / parts (e.g. an INE's Frente + Reverso). Empty = single-file document. ---
const ensureParts = target => {
  if (!Array.isArray(target.parts)) target.parts = [];
  return target.parts;
};
const addPart = target => ensureParts(target).push({ label: '' });
const updatePart = (target, idx, label) => {
  ensureParts(target)[idx].label = label;
};
const removePart = (target, idx) => ensureParts(target).splice(idx, 1);

const rejectingId = ref(null);
const rejectNote = ref('');

const emitCount = () => {
  const outstanding = requests.value.filter(
    r => r.required && (r.status === 'pending' || r.status === 'rejected')
  ).length;
  emit('count', outstanding);
};

const typeLabel = type =>
  TYPE_KEYS.includes(type) ? t(`PROJECTS.DOCUMENTS.TYPES.${type}`) : type;

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await props.api.get(props.ownerId);
    requests.value = data || [];
  } catch (error) {
    requests.value = [];
  } finally {
    isLoading.value = false;
    emitCount();
  }
};

const openForm = () => {
  form.value = emptyForm();
  showForm.value = true;
};
const cancelForm = () => {
  showForm.value = false;
};
const saveRequest = async () => {
  if (isSaving.value || !form.value.label.trim()) return;
  isSaving.value = true;
  try {
    await props.api.create(props.ownerId, form.value);
    showForm.value = false;
    await load();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const startEdit = request => {
  editingId.value = request.id;
  editForm.value = {
    label: request.label,
    document_type: TYPE_KEYS.includes(request.document_type)
      ? request.document_type
      : 'otro',
    required: request.required,
    parts: Array.isArray(request.parts)
      ? request.parts.map(part => ({ label: part.label }))
      : [],
  };
};
const cancelEdit = () => {
  editingId.value = null;
};
const saveEdit = async id => {
  if (!editForm.value.label.trim()) return;
  try {
    await props.api.update(props.ownerId, id, editForm.value);
    editingId.value = null;
    await load();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.SAVE_ERROR'));
  }
};

const removeRequest = async id => {
  try {
    await props.api.delete(props.ownerId, id);
    requests.value = requests.value.filter(r => r.id !== id);
    emitCount();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.DELETE_ERROR'));
  }
};

const accept = async id => {
  try {
    await props.api.accept(props.ownerId, id);
    await load();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.REVIEW_ERROR'));
  }
};
const startReject = id => {
  rejectingId.value = id;
  rejectNote.value = '';
};
const cancelReject = () => {
  rejectingId.value = null;
};
const confirmReject = async id => {
  if (!rejectNote.value.trim()) return;
  try {
    await props.api.reject(props.ownerId, id, rejectNote.value);
    rejectingId.value = null;
    await load();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.REVIEW_ERROR'));
  }
};

const onReorder = async () => {
  try {
    await props.api.reorder(
      props.ownerId,
      requests.value.map(r => r.id)
    );
  } catch (error) {
    await load();
  }
};

const copyUploadLink = async () => {
  if (isCopying.value) return;
  isCopying.value = true;
  try {
    const { data } = await props.shareFn(props.ownerId);
    const url = `${window.location.origin}/documents/${data.document_request_token}`;
    await copyTextToClipboard(url);
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.LINK_COPIED'));
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENT_REQUESTS.LINK_ERROR'));
  } finally {
    isCopying.value = false;
  }
};

watch(() => props.ownerId, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <Button
        v-if="!showForm"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('PROJECTS.DOCUMENT_REQUESTS.ADD')"
        @click="openForm"
      />
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-link"
        data-test="copy-link"
        :is-loading="isCopying"
        :label="t('PROJECTS.DOCUMENT_REQUESTS.COPY_LINK')"
        @click="copyUploadLink"
      />
    </div>

    <div
      v-if="showForm"
      class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-background"
    >
      <input
        v-model="form.label"
        type="text"
        :placeholder="t('PROJECTS.DOCUMENT_REQUESTS.LABEL_PLACEHOLDER')"
        class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
      />
      <div class="flex items-center gap-2">
        <label class="flex items-center gap-1.5 text-xs text-n-slate-10">
          {{ t('PROJECTS.DOCUMENT_REQUESTS.TYPE_LABEL') }}
          <select
            v-model="form.document_type"
            class="h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="key in TYPE_KEYS" :key="key" :value="key">
              {{ t(`PROJECTS.DOCUMENTS.TYPES.${key}`) }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-1.5 text-xs text-n-slate-11">
          <input v-model="form.required" type="checkbox" />
          {{ t('PROJECTS.DOCUMENT_REQUESTS.REQUIRED') }}
        </label>
      </div>

      <!-- Sides / parts: optional named slots for a multi-side document (e.g. INE). -->
      <div class="flex flex-col gap-1.5">
        <p class="text-xs text-n-slate-10">{{ t('PROJECTS.DOCUMENT_REQUESTS.SIDES_HINT') }}</p>
        <div
          v-for="(part, partIndex) in form.parts"
          :key="partIndex"
          class="flex items-center gap-2"
        >
          <input
            :value="part.label"
            type="text"
            :placeholder="t('PROJECTS.DOCUMENT_REQUESTS.SIDE_PLACEHOLDER')"
            class="flex-1 min-w-0 h-7 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            @input="updatePart(form, partIndex, $event.target.value)"
          />
          <Button
            type="button"
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-x"
            @click="removePart(form, partIndex)"
          />
        </div>
        <div>
          <Button
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-plus"
            :label="t('PROJECTS.DOCUMENT_REQUESTS.ADD_SIDE')"
            @click="addPart(form)"
          />
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('PROJECTS.DOCUMENT_REQUESTS.CANCEL')"
          @click="cancelForm"
        />
        <Button
          size="sm"
          color="blue"
          :label="t('PROJECTS.DOCUMENT_REQUESTS.SAVE')"
          :is-loading="isSaving"
          @click="saveRequest"
        />
      </div>
    </div>

    <ProjectEmptyState
      v-if="!requests.length && !isLoading"
      compact
      icon="i-lucide-file-check-2"
      :title="t('PROJECTS.DOCUMENT_REQUESTS.EMPTY')"
    />

    <Draggable
      v-else
      v-model="requests"
      item-key="id"
      handle=".req-drag"
      class="flex flex-col divide-y divide-n-weak/60"
      @end="onReorder"
    >
      <template #item="{ element: request }">
        <div data-test="req-row" class="flex flex-col gap-1.5 py-2.5">
          <div class="flex items-center gap-2">
            <span
              class="req-drag i-lucide-grip-vertical size-4 shrink-0 cursor-grab text-n-slate-9"
            />
            <template v-if="editingId === request.id">
              <input
                v-model="editForm.label"
                type="text"
                class="flex-1 h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
              />
              <Button
                size="xs"
                color="blue"
                :label="t('PROJECTS.DOCUMENT_REQUESTS.SAVE')"
                @click="saveEdit(request.id)"
              />
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :label="t('PROJECTS.DOCUMENT_REQUESTS.CANCEL')"
                @click="cancelEdit"
              />
            </template>
            <template v-else>
              <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
                {{ request.label }}
              </span>
              <span
                class="shrink-0 rounded-full px-1.5 py-0.5 text-xs font-medium"
                :class="STATUS_PILL[request.status] || STATUS_PILL.pending"
              >
                {{ t(`PROJECTS.DOCUMENT_REQUESTS.STATUS.${request.status}`) }}
              </span>
              <Button
                v-tooltip.top="t('PROJECTS.DOCUMENT_REQUESTS.EDIT')"
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-pencil"
                @click="startEdit(request)"
              />
              <Button
                v-tooltip.top="t('PROJECTS.DOCUMENT_REQUESTS.DELETE')"
                variant="ghost"
                color="ruby"
                size="xs"
                icon="i-lucide-x"
                @click="removeRequest(request.id)"
              />
            </template>
          </div>

          <!-- Sides / parts editor (edit mode) -->
          <div v-if="editingId === request.id" class="flex flex-col gap-1.5 pl-6">
            <p class="text-xs text-n-slate-10">{{ t('PROJECTS.DOCUMENT_REQUESTS.SIDES_HINT') }}</p>
            <div
              v-for="(part, partIndex) in editForm.parts"
              :key="partIndex"
              class="flex items-center gap-2"
            >
              <input
                :value="part.label"
                type="text"
                :placeholder="t('PROJECTS.DOCUMENT_REQUESTS.SIDE_PLACEHOLDER')"
                class="flex-1 min-w-0 h-7 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                @input="updatePart(editForm, partIndex, $event.target.value)"
              />
              <Button
                type="button"
                variant="ghost"
                color="ruby"
                size="xs"
                icon="i-lucide-x"
                @click="removePart(editForm, partIndex)"
              />
            </div>
            <div>
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-plus"
                :label="t('PROJECTS.DOCUMENT_REQUESTS.ADD_SIDE')"
                @click="addPart(editForm)"
              />
            </div>
          </div>

          <div class="flex items-center gap-2 pl-6 text-xs text-n-slate-10">
            <span>
              {{
                request.required
                  ? t('PROJECTS.DOCUMENT_REQUESTS.REQUIRED')
                  : t('PROJECTS.DOCUMENT_REQUESTS.OPTIONAL')
              }}
            </span>
            <span v-if="request.document_type">· {{ typeLabel(request.document_type) }}</span>
          </div>

          <a
            v-if="!request.parts?.length && request.document && request.document.file_url"
            :href="request.document.file_url"
            target="_blank"
            rel="noopener noreferrer"
            class="pl-6 text-sm truncate text-n-blue-text hover:underline"
          >
            {{ request.document.filename }}
          </a>

          <!-- Per-side files for a multi-part document -->
          <div v-if="request.parts?.length" class="flex flex-col gap-0.5 pl-6">
            <div
              v-for="part in request.parts_documents"
              :key="part.key"
              class="flex items-center gap-1.5 text-sm"
            >
              <span class="text-xs text-n-slate-10 shrink-0">{{ part.label }}:</span>
              <a
                v-if="part.file_url"
                :href="part.file_url"
                target="_blank"
                rel="noopener noreferrer"
                class="truncate text-n-blue-text hover:underline"
              >
                {{ part.filename }}
              </a>
              <span v-else class="text-xs text-n-slate-9">—</span>
            </div>
          </div>

          <p
            v-if="request.status === 'rejected' && request.review_note"
            class="pl-6 text-xs text-n-ruby-11"
          >
            {{ request.review_note }}
          </p>

          <div
            v-if="request.status === 'submitted' && rejectingId !== request.id"
            class="flex items-center gap-2 pl-6"
          >
            <Button
              size="xs"
              color="teal"
              :data-test="`accept-${request.id}`"
              :label="t('PROJECTS.DOCUMENT_REQUESTS.ACCEPT')"
              @click="accept(request.id)"
            />
            <Button
              variant="faded"
              color="ruby"
              size="xs"
              :data-test="`reject-${request.id}`"
              :label="t('PROJECTS.DOCUMENT_REQUESTS.REJECT')"
              @click="startReject(request.id)"
            />
          </div>

          <div v-if="rejectingId === request.id" class="flex flex-col gap-2 pl-6">
            <textarea
              v-model="rejectNote"
              rows="2"
              :placeholder="t('PROJECTS.DOCUMENT_REQUESTS.REJECT_NOTE_PLACEHOLDER')"
              class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            />
            <div class="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                color="slate"
                size="xs"
                :label="t('PROJECTS.DOCUMENT_REQUESTS.CANCEL')"
                @click="cancelReject"
              />
              <Button
                size="xs"
                color="ruby"
                :data-test="`confirm-reject-${request.id}`"
                :label="t('PROJECTS.DOCUMENT_REQUESTS.REJECT_CONFIRM')"
                @click="confirmReject(request.id)"
              />
            </div>
          </div>
        </div>
      </template>
    </Draggable>
  </div>
</template>
